import { createSelector } from '@reduxjs/toolkit';
import {
  addTagsToTagGroupRequest,
  deleteTagGroupRequest,
  fetchAllTagGroupsRequest,
  fetchSingleTagGroupRequest,
  fetchTagGroupsRequest,
  removeTagsFromTagGroupRequest,
  upsertTagGroupRequest,
} from 'actions/tagGroups';
import { defaultReviewer } from 'constants/reviewSets';
import { GlobalState } from 'reducers';
import type { NormalizedResource, SavedSearch, Selector, TagGroup, UUID } from 'types';

export const getFetchAllTagGroupsLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(fetchAllTagGroupsRequest.toString());

export const getFetchTagGroupsLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(fetchTagGroupsRequest.toString());

export const getFetchSingleTagGroupLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(fetchSingleTagGroupRequest.toString());

export const getSelectedTagGroup: Selector<TagGroup | null> = (state) =>
  state.tagGroups.selectedTagGroup;

export const getUpsertTagGroupLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(upsertTagGroupRequest.toString());

export const getDeleteTagGroupLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(deleteTagGroupRequest.toString());

export const getAddTagsToTagGroupLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(addTagsToTagGroupRequest.toString());

export const getRemoveTagsFromTagGroupLoading: Selector<boolean> = (state) =>
  state.tagGroups.loading.includes(removeTagsFromTagGroupRequest.toString());

export const getTagGroups: Selector<TagGroup[]> = createSelector(
  [(state: GlobalState): GlobalState['tagGroups']['items'] => state.tagGroups.items],
  (tagGroups) => Object.values(tagGroups)
);

export const getTagGroupsWithCounts: Selector<(TagGroup & { count: number })[]> = createSelector(
  [
    (state: GlobalState): GlobalState['tagGroups']['items'] => state.tagGroups.items,
    (state: GlobalState): GlobalState['envelopes']['filtersCounts'] =>
      state.envelopes.filtersCounts,
  ],
  (tagGroups, counts) => {
    const tagGroupsCounts = counts.tag_groups || [];

    return Object.values(tagGroups).map((t) => {
      // @ts-ignore
      const count = tagGroupsCounts?.find((c) => c.uuid === t.uuid)?.envelopes_count || 0;
      return { ...t, count };
    });
  }
);

export const getTagGroupById =
  (uuid: string): Selector<TagGroup | undefined> =>
  (state) =>
    Object.values(state.tagGroups.items).find((item) => item.uuid === uuid);

export const getFilterTagGroups: Selector<TagGroup[]> = createSelector(
  [(state: GlobalState): GlobalState['tagGroups']['filterItems'] => state.tagGroups.filterItems],
  (tagGroups) => Object.values(tagGroups)
);

export const getTagGroupsForSavedSearch: Selector<
  { childTagGroups: TagGroup[]; parentTagGroups: TagGroup[] },
  [UUID]
> = createSelector(
  [
    (state: GlobalState): GlobalState['savedSearches']['items'] => state.savedSearches.items,
    (state: GlobalState): GlobalState['tagGroups']['filterItems'] => state.tagGroups.filterItems,
    (_state: GlobalState, savedSearchId: string): string => savedSearchId,
  ],
  (savedSearches, tagGroupsFilterItems, savedSearchId) => {
    const tagGroups: { childTagGroups: TagGroup[]; parentTagGroups: TagGroup[] } = {
      childTagGroups: [],
      parentTagGroups: [],
    };

    const savedSearch = Object.values(savedSearches).reduce<SavedSearch | null>((acc, sSearch) => {
      if (sSearch.uuid === savedSearchId) {
        return sSearch;
      }
      if (sSearch.children && sSearch.children?.length > 0) {
        const foundChild = sSearch.children.find((child) => {
          if (child.uuid === savedSearchId) {
            return true;
          }
          return false;
        });
        if (foundChild) {
          return foundChild;
        }
      }
      return acc;
    }, null);

    if (savedSearch) {
      const tagGroupsIds = savedSearch.config?.tag_values_groups_uuids;
      const parentTagGroupsIds = savedSearch.parent?.config?.tag_values_groups_uuids || [];

      Object.values(tagGroupsFilterItems).forEach(
        (t) =>
          tagGroupsIds?.includes(t.uuid) &&
          !parentTagGroupsIds.includes(t.uuid) &&
          tagGroups.childTagGroups.push(t)
      );

      if (savedSearch.parent) {
        Object.values(tagGroupsFilterItems).forEach(
          (t) => parentTagGroupsIds?.includes(t.uuid) && tagGroups.parentTagGroups.push(t)
        );
      }
    }
    return tagGroups;
  }
);

export const getTagGroupsTotalCount: Selector<number> = (state) => state.tagGroups.count;

export const getTagGroupsForAssignment: Selector<TagGroup[], [UUID]> = createSelector(
  [
    (state: GlobalState): GlobalState['assignments']['items'] => state.assignments.items,
    (state: GlobalState): GlobalState['tagGroups']['filterItems'] => state.tagGroups.filterItems,
    (_state: GlobalState, assignmentId): string => assignmentId,
  ],
  (assignments, tagGroupsFilterItems, assignmentId) => {
    const assignment = assignments[assignmentId];

    if (!assignment) return [];

    const tagGroupsIds = assignment.saved_search?.config?.tag_values_groups_uuids;

    const tagGroups: TagGroup[] = [];

    Object.values(tagGroupsFilterItems).forEach(
      (t) => tagGroupsIds?.includes(t.uuid) && tagGroups.push(t)
    );

    return tagGroups;
  }
);

export const getTagGroupsForCustomer: Selector<TagGroup[]> = createSelector(
  [
    (state: GlobalState): string[] => state?.auth?.user?.customer?.config?.tag_group_uuids || [],
    (state: GlobalState): NormalizedResource<TagGroup> => state.tagGroups.filterItems,
    (state: GlobalState): string | null => state.envelopeReview.selectedReviewer,
  ],
  (customerTagGroups, filterItems, selectedReviewer) => {
    if (!customerTagGroups || customerTagGroups.length === 0) return [];

    const tagGroups: TagGroup[] = [];
    const shouldFilterModeler = selectedReviewer !== defaultReviewer.value;

    customerTagGroups.forEach((t) => {
      const group = Object.values(filterItems).find((g) => g.uuid === t);
      if (group) {
        tagGroups.push(group);
      }
    });

    return (
      tagGroups
        // @ts-ignore
        .filter((t) => t.tenant_name !== 'modeler' || !shouldFilterModeler)
    );
  }
);

export const getFurtherReviewTagGroup: Selector<TagGroup | null> = createSelector(
  [
    (state: GlobalState): string | undefined =>
      state?.auth?.user?.customer?.config?.further_review_tag_group || '',
    (state: GlobalState): NormalizedResource<TagGroup> => state.tagGroups.filterItems,
  ],
  (furtherReviewTagGroupId, filterItems) => {
    const furtherReviewTagGroup = Object.values(filterItems).find(
      (t) => t.uuid === furtherReviewTagGroupId
    );

    return furtherReviewTagGroup || null;
  }
);
